import { createSlice } from "@reduxjs/toolkit";
import { QualificationInterface } from "../../common/interfaces/QualificationInterface";
import { EmployeeInterface } from "../../common/interfaces/EmployeeInterface";
import { ValidityPeriodInterface } from "../../common/interfaces/ValidityPeriodInterface";
import { EducatorInterface } from "../../common/interfaces/EducatorInterface";
import { SectionEnum } from "../../common/enums/SectionEnum";
import { QualificationValidityTypeEnum } from "../../common/enums/QualificationValidityTypeEnum";

interface createCertificateModalInterface {
    isCreateCertificateModalOpen?: boolean;
    isPostCreateCertificateSuccess?: boolean;
    isAtQualification?: boolean;
    isEstablishmentCertificate?: boolean;
    selectedQualification?: QualificationInterface;
    selectedEmployee?: EmployeeInterface;
    formValues?: {
        qualification: {
            selectedId: string;
            selectedValue: string;
            validityPeriod: ValidityPeriodInterface | null;
            validityType: QualificationValidityTypeEnum | null;
            fixedValidUntil: string | null;
        };
        educators?: EducatorInterface[];
        employee: [selectedId: string, selectedValue: string];
        validAt: string;
        expiresAt: string;
        educator: {
            selectedId: string;
            selectedValue: string;
        };
    };
    error?: string;
    selectedEmployees: EmployeeInterface[];
    searchEmployees: string;
    formStep: number;
    validAtValues: { [key: number]: string };
    expiresAtValues: { [key: number]: string };
    isLoading: boolean;
    isFunctionGroupModalOpen?: boolean;
    isQualificationStandardModalOpen?: boolean;
    section: {
        name: SectionEnum;
        activeIndex: number;
    };
}

const initialState: createCertificateModalInterface = {
    isCreateCertificateModalOpen: false,
    selectedEmployees: [],
    searchEmployees: "",
    formStep: 1,
    validAtValues: {},
    expiresAtValues: {},
    isLoading: false,
    isFunctionGroupModalOpen: false,
    isQualificationStandardModalOpen: false,
    section: {
        name: SectionEnum.PERSONS_CERTIFICATE,
        activeIndex: 0,
    },
};

const CreateCertificateModalSlice = createSlice({
    name: "createCertificateModal",
    initialState,
    reducers: {
        setIsCreateCertificateModalOpen: (state, action) => {
            state.isCreateCertificateModalOpen = action.payload;
        },
        setIsPostCreateCertificateSuccess: (state, action) => {
            state.isPostCreateCertificateSuccess = action.payload;
        },
        setSelectedQualification: (state, action) => {
            state.selectedQualification = action.payload;
        },
        setSelectedEmployee: (state, action) => {
            state.selectedEmployee = action.payload;
        },
        setFormValues: (state, action) => {
            state.formValues = action.payload;
        },
        resetFormValues: (state) => {
            state.formValues = {
                qualification: {
                    selectedId: "",
                    selectedValue: "",
                    validityPeriod: {
                        id: 0,
                        months: 0,
                        years: 0,
                        name: "",
                        readable: "",
                    },
                    validityType: null,
                    fixedValidUntil: null,
                },
                educators: [],
                employee: ["", ""],
                validAt: "",
                expiresAt: "",
                educator: { selectedId: "", selectedValue: "" },
            };
            state.selectedEmployee = undefined;
            state.selectedQualification = undefined;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setIsAtQualification: (state, action) => {
            state.isAtQualification = action.payload;
        },
        setIsEstablishmentCertificate: (state, action) => {
            state.isEstablishmentCertificate = action.payload;
        },
        setSelectedEmployees: (state, action) => {
            state.selectedEmployees = action.payload;
        },
        setSearchEmployees: (state, action) => {
            state.searchEmployees = action.payload;
        },
        setFormStep: (state, action) => {
            state.formStep = action.payload;
        },
        setValidAtValue: (state, action) => {
            const { employeeId, validAt } = action.payload;
            state.validAtValues[employeeId] = validAt;
        },
        setExpiresAtValue: (state, action) => {
            const { employeeId, expiresAt } = action.payload;
            state.expiresAtValues[employeeId] = expiresAt;
        },
        setIsLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        updateCertificateModalState: (state, action) => {
            return { ...state, ...action.payload };
        },
        setIsFunctionGroupModalOpen: (state, action) => {
            state.isFunctionGroupModalOpen = action.payload;
        },
        setIsQualificationStandardModalOpen: (state, action) => {
            state.isQualificationStandardModalOpen = action.payload;
        },
        setSection: (state, action) => {
            state.section = action.payload;
        },
    },
});

export const createCertificateModalReducer =
    CreateCertificateModalSlice.reducer;

export const {
    setIsCreateCertificateModalOpen,
    setSelectedQualification,
    setSelectedEmployee,
    setFormValues,
    resetFormValues,
    setError,
    setIsAtQualification,
    setIsPostCreateCertificateSuccess,
    setSelectedEmployees,
    setSearchEmployees,
    setFormStep,
    setValidAtValue,
    setExpiresAtValue,
    setIsLoading,
    updateCertificateModalState,
    setIsFunctionGroupModalOpen,
    setIsQualificationStandardModalOpen,
    setSection,
    setIsEstablishmentCertificate,
} = CreateCertificateModalSlice.actions;
